/**
 * auth API — FO 인증·회원가입 도메인 함수(003 member-signup 실 연동, 로컬 아이디/비밀번호 전용).
 *
 * 모든 호출은 단일 axios 인스턴스(`http`, withCredentials)를 경유한다 — 직접 axios/fetch 금지(헌법 I).
 * 글로벌 에러 처리는 http.ts 인터셉터 1회로 일원화 — 호출부 catch 에서 중복 alert 금지.
 *
 * 인증 엔드포인트:
 *   POST   /api/fo/auth/signup           — 아이디/비밀번호 가입(201 + Set-Cookie — 즉시 로그인).
 *   POST   /api/fo/auth/login            — 로그인(200 + Set-Cookie).
 *   GET    /api/fo/auth/me               — 세션 확인.
 *   DELETE /api/fo/auth/logout           — 세션 종료.
 *
 * 세션은 응답 본문이 아니라 HttpOnly 세션 쿠키(IPSI_SESSION_FO)로 전달된다 — 가입·로그인이 같은 메커니즘이다.
 */

import { http, type SilentRequestConfig } from '#/api/http';

/** 가입 동의 묶음(백엔드 SignupAgreements). */
export interface SignupAgreements {
  /** (필수) 만 14세 이상. */
  age14: boolean;
  /** (필수) 이용약관 동의. */
  terms: boolean;
  /** (필수) 개인정보 수집·이용 동의. */
  privacy: boolean;
  /** (선택③) 마케팅 활용 동의 — 회사가 연락하는 것. */
  marketing: boolean;
  /**
   * (선택②) 맞춤 정보 수집·이용 동의 — 화면 안에서 보여주는 것.
   * 마케팅(③)과 독립이며, 미동의여도 가입·구매·다운로드에 제한이 없다(개보법 §22⑤).
   */
  profileSegment: boolean;
}

/**
 * 가입 선택② 세그먼트(백엔드 SignupSegment) — 설계 10번 §4.3.
 *
 * ⚠️ **전 필드 선택 입력이다.** 미입력·미전송이어도 가입이 완료돼야 하며, 화면에서 필수 검증에
 * 넣으면 개보법 §22⑤ 위반이 된다. 미입력은 `null`/`''` 로 그대로 보낸다.
 */
export interface SignupSegment {
  /** 이용자 유형(STUDENT·PARENT·TEACHER·COUNSELOR·INSTRUCTOR·ETC). CONSULTANT 는 선택지 미노출(V951). */
  memberRole: string | null;
  /** 기타 자유입력 — 역할이 ETC 일 때만 서버가 반영한다. */
  memberRoleEtc: string | null;
  /** 학년(H1·H2·H3·N) — 학생=본인, 학부모=자녀. */
  studentGrade: string | null;
  /** 거주 시도 코드(SEOUL·GYEONGGI… 17종). */
  region: string | null;
  /** 고교 마스터 식별자(자동완성 선택) — 정상 경로. */
  schoolId: number | null;
  /** 마스터 미매칭 학교명 — schoolId 가 있으면 서버가 버린다(배타). */
  schoolEtc: string | null;
}

/** 아이디/비밀번호 회원가입 요청(백엔드 LocalSignupRequest). */
export interface LocalSignupPayload {
  /** 로그인 아이디(^[a-z][a-z0-9_]{3,19}$). */
  username: string;
  password: string;
  passwordConfirm: string;
  email: string;
  /** 이름(필수, trim 후 2~20자) — `sys_user.nick_name` 에 저장된다(053). */
  name: string;
  agreements: SignupAgreements;
  /** 선택② 세그먼트 — 미입력이면 전부 null 로 보낸다(가입은 그대로 완료). */
  segment: SignupSegment;
}

/** 회원가입 성공 응답(백엔드 SignupResponse). */
export interface SignupResult {
  /** 발급된 로그인 아이디(입력값). */
  username: string;
  /**
   * 가입 대상 이메일 — 완료 안내가 "어느 주소로 가입했는지"를 표시하는 데 쓴다.
   * 사용자가 방금 제출한 값이라 새로 노출되는 정보가 없다.
   */
  email: string;
}

/**
 * 로컬 로그인 요청 페이로드 — 아이디/비밀번호.
 * 식별자는 회원가입(username 으로 가입)과 정합하도록 username 을 쓴다(이메일 아님).
 */
export interface LoginPayload {
  username: string;
  password: string;
}

/** 로그인 성공 응답(백엔드 FoLoginResponse) — 로그인한 아이디. */
export interface LoginResult {
  username: string;
}

/**
 * 아이디/비밀번호 로그인 — `POST /api/fo/auth/login`.
 * 성공 시 백엔드가 HttpOnly 세션 쿠키(IPSI_SESSION_FO)를 내려 즉시 로그인 상태가 된다(200). 본문은 username 만 반환한다.
 * 실패는 사유 구분 없이 401(일반화 — 계정 열거 방지)이며, 글로벌 에러는 http.ts 인터셉터가 토스트로 처리한다(호출부는 흐름 제어만).
 */
export async function login(payload: LoginPayload): Promise<LoginResult> {
  const { data } = await http.post<LoginResult>('/fo/auth/login', payload);
  return data;
}

/**
 * 아이디/비밀번호 회원가입 — `POST /api/fo/auth/signup`.
 *
 * 성공 시 백엔드가 로그인과 **동일한 HttpOnly 세션 쿠키**를 함께 내려 즉시 로그인 상태가 된다(201).
 * 글로벌 에러(400 검증·409 중복)는 http.ts 인터셉터가 토스트로 처리 — 호출부는 흐름 제어만.
 */
export async function signup(payload: LocalSignupPayload): Promise<SignupResult> {
  const { data } = await http.post<SignupResult>('/fo/auth/signup', payload);
  return data;
}

// ── 세션 ──────────────────────────────────────────────────────────────────

/** 세션 확인 응답 — 유효 세션이면 인증 정보, 미인증이면 null. */
export interface SessionInfo {
  /** 인증 여부(유효 세션이면 true). */
  authenticated: boolean;
  /** 로그인 사용자 이메일. */
  email: string;
}

/**
 * FO 세션 확인 — 영속 쿠키(IPSI_SESSION_FO)만으로 로그인 상태를 복원한다(FR-017·SC-011).
 *
 * 앱 부팅 시 1회 호출해, 쿠키 세션이 살아있으면 SessionInfo(200), 미로그인·만료면 null(401)을 반환한다.
 * 미로그인 401 은 **기대된 정상 응답**이므로 `silent` 로 전역 토스트·clearSession 을 우회한다
 * (http.ts 인터셉터가 silent 요청의 에러를 조용히 전파). 401 외 에러(네트워크 등)는 호출부가 null 로 흡수해
 * 부팅이 막히지 않게 한다.
 */
export async function fetchSession(): Promise<SessionInfo | null> {
  try {
    const { data } = await http.get<SessionInfo>('/fo/auth/me', {
      silent: true,
    } as SilentRequestConfig);
    return data;
  } catch {
    // 401(미로그인)·네트워크 오류 등 모든 실패를 "복원할 세션 없음"으로 흡수 — 부팅을 막지 않는다.
    // (보호 라우트 접근은 가드가 guest 로 별도 차단하므로 안전.)
    return null;
  }
}

/**
 * FO 로그아웃 — 서버 세션(HttpOnly IPSI_SESSION_FO 쿠키)을 백엔드에서 종료한다.
 * `DELETE /api/fo/auth/logout` 가 Redis 세션을 무효화하고 쿠키를 만료(Set-Cookie Max-Age=0)한다.
 * 프론트 로컬 상태 초기화만으로는 서버 세션·쿠키가 잔존하므로 반드시 이 호출 후 스토어를 정리한다.
 * 글로벌 에러는 http.ts 인터셉터가 처리 — 호출부는 흐름 제어만 담당.
 */
export async function logout(): Promise<void> {
  await http.delete('/fo/auth/logout');
}
